async function fetchStores() {
  try {
    const res = await fetch('/?section_id=store-locator-data');
    const text = await res.text();
    const html = new DOMParser().parseFromString(text, 'text/html');
    const jsonElement = html.querySelector('[data-store-json]');
    if (!jsonElement) throw new Error('Missing store JSON container');
    const json = JSON.parse(jsonElement.textContent);
    return json.stores || [];
  } catch (err) {
    console.error('Failed to fetch stores:', err);
    return [];
  }
}

class StoreLocator extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  async init() {
    const selected = sessionStorage.getItem('selectedStore');
    if (selected) {
      this.updateUI(JSON.parse(selected));
      return;
    }

    const stores = await fetchStores();
    const userLoc = await this.fetchUserLocation();

    if (!userLoc || !stores.length) {
      this.updateUI(null);
      return;
    }

    const nearest = this.findNearestStore(stores, userLoc);
    sessionStorage.setItem('selectedStore', JSON.stringify(nearest));
    this.updateUI(nearest);
  }

  async fetchUserLocation() {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            console.log('[Store Locator] Location method: HTML5 Geolocation');
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          async () => {
            // Fallback to IP-based location
            try {
              const res = await fetch('https://ipinfo.io/json?token=77fdef0effcecf');
              const data = await res.json();
              const [lat, lng] = data.loc.split(',').map(Number);
              console.log('[Store Locator] Location method: IP-based fallback');
              resolve({ lat, lng });
            } catch {
              console.warn('[Store Locator] Failed to get location via both HTML5 and IP fallback');
              resolve(null);
            }
          },
          { timeout: 5000 }
        );
      } else {
        console.warn('[Store Locator] Geolocation not supported, skipping to fallback');
        resolve(null);
      }
    });
  }    

  findNearestStore(stores, userLoc) {
    const userPoint = turf.point([userLoc.lng, userLoc.lat]);
    const storePoints = stores.map((s) =>
      turf.point([s.longitude, s.latitude], s)
    );
    const nearest = turf.nearestPoint(userPoint, turf.featureCollection(storePoints));
    return nearest.properties;
  }
  
  updateUI(store) {
    const output = this.querySelector('.store-locator__output');
  
    if (!output) return;
  
    if (!store) {
      output.innerHTML = `
        There was a problem with your geo-localization
        <a href="/pages/store-locator" class="store-locator__change-link">
          Select store manually
        </a>
      `;
      return;
    }
  
    output.innerHTML = `
      Your nearest store is: 
      <span class="store-locator__name">
        ${store.name}
        <div class="custom-tooltip">
          ${store.address}<br>
          ${store.city}, ${store.state} ${store.zip_code}
        </div>
      </span>
      <a href="/pages/store-locator" class="store-locator__change-link">Change</a>
    `;
  }
}

customElements.define('store-locator', StoreLocator);

class StoreLocatorUI extends HTMLElement {
  constructor() {
    super();
    this.stores = [];
    this.map = null;
    this.markers = [];
  }

  connectedCallback() {
    this.innerHTML = `
      <div class="store-locator-ui">
        <div class="store-locator-ui__form">
          <div class="field field-locator">
            <input type="text" id="store-search-input" class="search__input field__input" />
            <label class="field__label" for="store-search-input">Enter city, address or zip code</label>
          </div>
        </div>
        <div class="store-locator-ui__mobile-toggle">
          <button data-view="list" class="is-active">List</button>
          <button data-view="map">Map</button>
        </div>
        <div class="store-locator-ui__content">
          <ul id="store-locator-list" class="store-locator-ui__list"></ul>
          <div id="store-locator-map" class="store-locator-ui__map"></div>
        </div>
      </div>
    `;
    this.init();
  }  

  async init() {
    Radar.initialize('prj_test_pk_c7a710eb6daac812c82f561cbd2761e76a809e21');
  
    this.stores = await fetchStores();
  
    this.map = Radar.ui.map({
      container: 'store-locator-map',
      style: 'radar-default-v1',
      center: [-98.5795, 39.8283],
      zoom: 4
    });
  
    setTimeout(() => this.map.resize(), 0);
  
    this.renderStores(this.stores);

    const input = this.querySelector('#store-search-input');
    const autocomplete = new google.maps.places.Autocomplete(input, {
      types: ['geocode'],
      componentRestrictions: { country: 'us' }
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry || !place.geometry.location) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const userPoint = turf.point([lng, lat]);

      const sortedStores = this.stores
        .map(store => {
          const distance = turf.distance(userPoint, turf.point([store.longitude, store.latitude]));
          return { ...store, distance };
        })
        .sort((a, b) => a.distance - b.distance);

      this.renderStores(sortedStores);

      if (sortedStores.length > 0) {
        const first = sortedStores[0];
        this.map.setCenter([first.longitude, first.latitude]);
        this.map.setZoom(10);
      }
    });
  
    const toggleButtons = this.querySelectorAll('.store-locator-ui__mobile-toggle button');
    const listEl = this.querySelector('.store-locator-ui__list');
    const mapEl = this.querySelector('.store-locator-ui__map');
  
    const setView = (view) => {
      toggleButtons.forEach(btn => btn.classList.remove('is-active'));
      this.querySelector(`.store-locator-ui__mobile-toggle button[data-view="${view}"]`)?.classList.add('is-active');
  
      if (view === 'list') {
        listEl.classList.add('is-visible');
        mapEl.classList.remove('is-visible');
      } else {
        listEl.classList.remove('is-visible');
        mapEl.classList.add('is-visible');
      }
    };
  
    setView('list');
  
    toggleButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const view = btn.dataset.view;
        setView(view);
      });
    });
  }  

  renderStores(storeList) {
    const list = this.querySelector('#store-locator-list');
    list.innerHTML = '';
    this.clearMarkers();

    storeList.forEach((store) => {
      if (
        typeof store.longitude !== 'number' ||
        typeof store.latitude !== 'number' ||
        isNaN(store.longitude) ||
        isNaN(store.latitude)
      ) {
        console.warn('Skipping store with invalid coordinates:', store);
        return;
      }
    
      const li = document.createElement('li');
      li.innerHTML = `
        <strong>${store.name}</strong><br>
        ${store.address}<br>
        ${store.city}, ${store.state} ${store.zip_code}
        <br><button data-store='${JSON.stringify(store)}'>Select</button>
      `;
      list.appendChild(li);
    
      const marker = Radar.ui.marker({
        popup: {
          html: `<strong>${store.name}</strong><br>${store.address}`
        }
      })
      .setLngLat([store.longitude, store.latitude])
      .addTo(this.map);
    
      li.querySelector('button').addEventListener('click', () => {
        sessionStorage.setItem('selectedStore', JSON.stringify(store));
      
        const referrer = sessionStorage.getItem('storeLocatorReferrer');
      
        if (referrer) {
          window.location.href = referrer;
        } else if (document.referrer && document.referrer.includes(window.location.hostname)) {
          window.history.back();
        } else {
          window.location.href = '/';
        }
      });
      
    });    

    if (storeList.length > 0) {
      this.map.setCenter([storeList[0].longitude, storeList[0].latitude]);
      this.map.setZoom(10);
    }
  }

  clearMarkers() {
    this.markers.forEach(marker => marker.remove());
    this.markers = [];
  }
}

customElements.define('store-locator-ui', StoreLocatorUI);
